/**
 * Actual VoiceBot surface mount registry and compact resource inventories.
 * Registration reflects React mount/unmount, not logical product-stage flags.
 */

import { useEffect } from 'react';

export const SURFACE_IDS = Object.freeze({
  FIRST_TIME_WELCOME: 'firstTimeWelcome',
  CURTAIN: 'curtainLoader',
  THEATRE_SCRIM: 'theatreScrim',
  INTRO: 'introTrainer',
  SOLAR: 'solarTrainer',
  STUDIO_SET: 'studioSet',
  AVATAR: 'avatarIframe',
  YOUTUBE: 'youtube',
  OPTIMUS: 'optimusReveal',
  TUNING: 'tuningModal',
  ASSESSMENT: 'assessment',
  TALK_CHAT: 'talkChat',
  FLUENCY_TOOLS: 'fluencyTools',
  TRANSCRIPT: 'transcriptTrainer',
  DISPLAY_CAPTURE: 'displayCapture',
  DIAL: 'dial',
  BUTTON_GRID: 'buttonGrid',
});

const mounts = Object.create(null);

export function resetVoiceBotSurfacesForTests() {
  Object.keys(mounts).forEach((k) => { delete mounts[k]; });
}

export function registerVoiceBotSurface(id) {
  if (!id) return;
  mounts[id] = (mounts[id] || 0) + 1;
}

export function unregisterVoiceBotSurface(id) {
  if (!id) return;
  const next = Math.max(0, (mounts[id] || 0) - 1);
  if (next) mounts[id] = next;
  else delete mounts[id];
}

export function getActualResidency() {
  const out = {};
  Object.keys(SURFACE_IDS).forEach((key) => {
    const id = SURFACE_IDS[key];
    out[id] = (mounts[id] || 0) > 0;
  });
  return out;
}

export function getSurfaceMountCounts() {
  return { ...mounts };
}

export function useVoiceBotSurface(id, active = true) {
  useEffect(() => {
    if (!id || !active) return undefined;
    registerVoiceBotSurface(id);
    return () => unregisterVoiceBotSurface(id);
  }, [id, active]);
}

export function VoiceBotSurface({ id, active = true, children }) {
  useVoiceBotSurface(id, active);
  return children;
}

const SENSITIVE_QUERY = /token|auth|key|uid|email|id_token|access_token/i;

export function sanitizeIframeSrc(raw) {
  if (!raw || typeof raw !== 'string') {
    return { origin: null, pathname: null };
  }
  try {
    const url = new URL(raw, typeof window !== 'undefined' ? window.location.href : 'https://ispeakwell.ca');
    const params = new URLSearchParams();
    url.searchParams.forEach((value, key) => {
      if (SENSITIVE_QUERY.test(key)) params.set(key, '[redacted]');
      else if (key.startsWith('vb')) params.set(key, value);
    });
    const q = params.toString();
    return {
      origin: url.origin,
      pathname: url.pathname + (q ? `?${q}` : ''),
    };
  } catch {
    return { origin: null, pathname: null };
  }
}

export function inferIframeRole({ origin, pathname, tagged, id, title } = {}) {
  if (tagged) return tagged;
  const hay = `${origin || ''}${pathname || ''}`.toLowerCase();
  if (hay.includes('recaptcha')) return 'recaptcha';
  if (
    hay.includes('googletagmanager')
    || hay.includes('doubleclick')
    || hay.includes('googleads')
    || hay.includes('google.com/pagead')
    || hay.includes('google.com/ads')
  ) return 'gtag-ads';
  if (hay.includes('youtube.com') || hay.includes('youtube-nocookie')) return 'youtube';
  if (hay.includes('voicebot.ispeakwell.ca') && hay.includes('fluency')) return 'fluency-tools';
  if (hay.includes('voicebot.ispeakwell.ca')) return 'avatar';
  if (hay.includes('firebaseapp.com') && hay.includes('/__/auth')) return 'firebase-auth';
  if (hay.includes('chatapp.ispeakwell.ca')) return 'talk-chat';
  if (id === 'theBot') return 'avatar';
  if (id === 'theBotTools') return 'fluency-tools';
  if (id || title) return id || title;
  return 'unknown';
}

function ownerForIframe(iframe, parsed) {
  if (!iframe) return null;
  const tagged = (iframe.dataset && iframe.dataset.vbOwner)
    || (iframe.closest && iframe.closest('[data-vb-owner]')
      && iframe.closest('[data-vb-owner]').getAttribute('data-vb-owner'))
    || null;
  return inferIframeRole({
    origin: parsed && parsed.origin,
    pathname: parsed && parsed.pathname,
    tagged,
    id: iframe.id || null,
    title: iframe.title || null,
  });
}

export function collectIframeInventory() {
  if (typeof document === 'undefined') return [];
  const list = document.getElementsByTagName('iframe');
  const out = [];
  for (let i = 0; i < list.length; i++) {
    const el = list[i];
    const src = el.getAttribute('src') || el.src || '';
    const parsed = sanitizeIframeSrc(src);
    let visible = false;
    try {
      const cs = window.getComputedStyle(el);
      visible = cs.display !== 'none'
        && cs.visibility !== 'hidden'
        && cs.opacity !== '0'
        && el.getClientRects().length > 0;
    } catch {
      visible = false;
    }
    out.push({
      slot: i,
      id: el.id || null,
      owner: ownerForIframe(el, parsed),
      origin: parsed.origin,
      pathname: parsed.pathname,
      visible,
      connected: !!el.isConnected,
      width: el.clientWidth || 0,
      height: el.clientHeight || 0,
    });
  }
  return out;
}

function collectTagInventory(tag) {
  if (typeof document === 'undefined') return [];
  const list = document.getElementsByTagName(tag);
  const out = [];
  for (let i = 0; i < list.length; i++) {
    const el = list[i];
    out.push({
      slot: i,
      id: el.id || null,
      owner: el.id || null,
      connected: !!el.isConnected,
      width: el.clientWidth || 0,
      height: el.clientHeight || 0,
    });
  }
  return out;
}

export function collectMediaInventory() {
  const p5Count = (typeof window !== 'undefined' && window.p5 && window.p5.instances)
    ? window.p5.instances.length
    : (typeof window !== 'undefined' && Array.isArray(window._p5Instances) ? window._p5Instances.length : null);
  return {
    video: collectTagInventory('video'),
    audio: collectTagInventory('audio'),
    canvas: collectTagInventory('canvas'),
    p5InstanceCount: p5Count,
  };
}

export function collectResourceInventory() {
  const mem = (typeof performance !== 'undefined' && performance.memory) ? performance.memory : null;
  return {
    available: typeof document !== 'undefined',
    domNodes: typeof document !== 'undefined' ? document.getElementsByTagName('*').length : 0,
    jsHeapUsed: mem ? mem.usedJSHeapSize : null,
    jsHeapTotal: mem ? mem.totalJSHeapSize : null,
    residencyActual: getActualResidency(),
    iframes: collectIframeInventory(),
    media: collectMediaInventory(),
  };
}
